// "Six products' worth of help, in one crew member." (TOM-980): the approved
// design's animation of Neo's six kinds of help. The video and its poster load
// only when the section comes within 600 px of the viewport, so a visit that
// never scrolls there downloads neither. It plays muted and looped. Under
// reduced motion it stays paused on its poster with controls. If the browser
// refuses to play it, the controls appear so the visitor can start it.
import { useEffect, useRef, useState } from 'react';

import { useReducedMotion } from '../../lib/useReducedMotion';

const VIDEO_SRC = '/home/neo-six-kinds.mp4';
const POSTER_SRC = '/home/neo-six-kinds-poster.jpg';

const VIDEO_LABEL =
  "Animation of Neo's six kinds of help: Conversational, natural-language access to vessel information, history and guidance. Proactive, continuous analysis that can catch problems before conventional alarms go off. Contextual, reads multiple sensors, conditions and records together. Remembers, knowledge that grows around your boat and its voyages. Approachable, as useful to a first-time guest as to an experienced captain. Brand-independent, compatible with equipment from multiple marine electronics makers.";

export function HomeVideo() {
  const reducedMotion = useReducedMotion();
  const ref = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(false);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = ref.current;
    if (!video || !near) return;
    video.muted = true;
    if (reducedMotion) {
      video.pause();
      return;
    }
    let cancelled = false;
    video.play().catch(() => {
      if (!cancelled) setRefused(true);
    });
    return () => {
      cancelled = true;
    };
  }, [near, reducedMotion]);

  return (
    <section className="home-band home-band--page" aria-labelledby="home-video-title">
      <div className="home-wrap home-stack home-stack--video">
        <h2 id="home-video-title" className="home-h2 home-h2--narrow">
          Six products&apos; worth of help, in one crew member.
        </h2>
        <video
          ref={ref}
          className="home-video"
          src={near ? VIDEO_SRC : undefined}
          poster={near ? POSTER_SRC : undefined}
          aria-label={VIDEO_LABEL}
          muted
          loop
          playsInline
          autoPlay={!reducedMotion}
          preload={reducedMotion ? 'metadata' : 'auto'}
          controls={reducedMotion || refused}
        />
      </div>
    </section>
  );
}
